import { useNavigate } from 'react-router-dom';
import { useAudioListening } from '@features/dictation/listening';
import { FeatureGates } from '@services/statsig/constants';
import { useFeatureGate } from '@services/statsig/hooks';
import { useEventTracking } from '@services/statsig/hooks/useEventTracking';

import { desktopDownloads, desktopPlatform } from '../desktop/desktopAvailability';
import type { SupportTransport } from '../hooks/useSupportRequest';
import { useCheckSupportRequest, useSubmitSupportRequest } from '../store/api';
import { useJotSupportPlacement } from '../store/useJotSupportPlacement';
import { submissionRejection } from '../submissionRejection';
import type { SupportInteraction, SupportRequest } from '../types';

import { JotSupportFlow } from './JotSupportFlow';
import type { JotSupportDisplayProps } from './JotSupportWidget';

/** Authenticated transport mounted through the shared Front visibility wrapper. */
export function JotSupportContainer({
  replyEmail,
  pageArea = 'other',
  ...display
}: JotSupportDisplayProps & {
  replyEmail: string;
  pageArea?: SupportRequest['page_area'];
}) {
  const [submitRequest] = useSubmitSupportRequest();
  const [checkRequest] = useCheckSupportRequest();
  const { jotSupport, jotDesktop } = useEventTracking();
  const navigate = useNavigate();
  const platform = desktopPlatform();
  const desktopEnabled = useFeatureGate(FeatureGates.JOT_DESKTOP_COMPANION);
  const { placement, savePlacement } = useJotSupportPlacement();
  const transport: SupportTransport = {
    submit: async request => {
      const operation = submitRequest({ supportRequestCreate: request });
      try {
        return await operation.unwrap();
      } catch (error) {
        throw submissionRejection(error) ?? error;
      } finally {
        operation.reset();
      }
    },
    check: async requestId => {
      const result = checkRequest({ requestId }, false);
      try {
        return await result.unwrap();
      } finally {
        result.unsubscribe();
      }
    },
  };
  const track = (event: SupportInteraction) => {
    const actions = {
      opened: jotSupport.opened,
      closed: jotSupport.closed,
      submitted: jotSupport.submitted,
      retry_clicked: jotSupport.retryClicked,
      status_requested: jotSupport.statusRequested,
      attachment_selected: jotSupport.attachmentSelected,
    };
    actions[event]();
  };
  const listening = useAudioListening();
  return (
    <JotSupportFlow
      {...display}
      listening={listening}
      onDownloadDesktop={
        desktopEnabled && platform && desktopDownloads[platform]
          ? () => {
              jotDesktop.toolbarDownloadClicked();
              navigate('/desktop-jot');
            }
          : undefined
      }
      placement={placement}
      onPlacementChange={(next, change) => {
        savePlacement(next);
        if (change.action === 'dock') jotSupport.dockClicked();
        else if (change.action === 'undock') jotSupport.undockClicked();
        else jotSupport.positionChanged(change.method);
      }}
      transport={transport}
      replyEmail={replyEmail}
      pageArea={pageArea}
      onInteraction={track}
    />
  );
}
